// Omnibar sigil layer — the DISCOVERABLE mode grammar over the shipped search
// engine (`query.ts`). The leading character of the query selects a MODE (`>`
// commands, `@` resources, none = everything); the rest is an ordinary
// `SearchQuery` run by the same fuzzy `evaluate`. Convention borrowed wholesale
// from VS Code / Raycast / Slack so power users arrive with the muscle memory.
// Spec: .claude/analysis/omnibar-dsl.md.
//
// This module owns ONLY the sigil/mode + kind-prefix-refinement layer; it reuses
// `tokenize`/`termOf`/`SEARCH_KINDS`/`SearchQuery` from `query.ts` (one tokenizer,
// one scorer — no re-derivation). A sigil desugars: `@s:acids` ≡
// `type:source acids`, so `parseOmni` emits a real `SearchQuery` the existing
// `evaluate` runs unchanged. Mode gating (`>` = commands only, `@` = drop
// commands) happens at the GROUPING layer (`modeAdmits`), because `command` is
// not a `SearchKind` and cannot be a `type:` filter.
//
// Pure + TOTAL, exactly like the engine it wraps: a box mid-keystroke is always
// a valid query, so an ambiguous/unknown kind prefix DEGRADES to text (never an
// error) and the hint UI names the valid kinds. No throws, no casts.

import {
    SEARCH_KINDS,
    type SearchKind,
    type SearchQuery,
    type SearchTerm,
    type Token,
    termOf,
    tokenize,
} from "./query.js";

export type { KindResolution, ParsedOmni, QueryMode };
export {
    leadingRefinement,
    modeAdmits,
    parseOmni,
    resolveKindPrefix,
    shortestUniquePrefix,
};

// === QueryMode — a named sum, selected by the leading sigil ==================
type Everything = { readonly type: "everything" }; // no sigil
type Commands = { readonly type: "commands" }; // `>`
type DevCommands = { readonly type: "dev_commands" }; // `>>`
type Resources = { readonly type: "resources" }; // `@`
type QueryMode = Everything | Commands | DevCommands | Resources;

// The parsed omnibar query: the mode plus a real `SearchQuery` the shipped
// `evaluate` consumes as-is. The mode is the ONLY new axis; everything under it
// is the engine's existing term sum.
type ParsedOmni = {
    readonly mode: QueryMode;
    readonly query: SearchQuery;
};

// === Kind resolution — a named sum, never a flag =============================
// Generic over the kind string type `K` so the same total logic serves the
// production `SearchKind` list AND a test-injected colliding list — no cast to
// fabricate a collision. The public `KindResolution` (no arg) is the concrete
// `SearchKind` shape the hint UI consumes.
type KindResolution<K extends string = SearchKind> =
    | { readonly type: "resolved"; readonly kind: K }
    | { readonly type: "ambiguous"; readonly candidates: readonly K[] } // >= 2
    | { readonly type: "unknown" };

// Resolve a kind PREFIX against the kind list by shortest unique prefix.
//   - a full-word (case-insensitive exact) match always resolves, even were it a
//     prefix of another kind;
//   - exactly one kind starting with the prefix → that kind;
//   - two or more → Ambiguous (surface the candidates);
//   - none → Unknown.
// Total: any string in, a KindResolution out. Derived from the kind list — a new
// kind auto-joins and uniqueness recomputes, nothing is hardcoded per kind.
const resolveKindPrefix = <K extends string>(
    prefix: string,
    kinds: readonly K[],
): KindResolution<K> => {
    const p = prefix.toLowerCase();
    const exact = kinds.find((k) => k.toLowerCase() === p);
    if (exact !== undefined) return { type: "resolved", kind: exact };
    const candidates = kinds.filter((k) => k.toLowerCase().startsWith(p));
    if (candidates.length === 0) return { type: "unknown" };
    if (candidates.length === 1)
        return { type: "resolved", kind: candidates[0] };
    return { type: "ambiguous", candidates };
};

// The shortest prefix of `kind` unique among `kinds` — the exact short form to
// type (`@s` for source). Used by the ghost autocomplete + the disambiguation
// hint. Derived, never hardcoded.
const shortestUniquePrefix = <K extends string>(
    kind: K,
    kinds: readonly K[],
): string => {
    for (let n = 1; n <= kind.length; n += 1) {
        const p = kind.slice(0, n);
        if (kinds.filter((k) => k.startsWith(p)).length === 1) return p;
    }
    return kind;
};

// === Mode split — strip the leading sigil ====================================
type ModeSplit = { readonly mode: QueryMode; readonly rest: string };

const splitMode = (raw: string): ModeSplit => {
    // `>>` (dev commands) is checked BEFORE `>` (commands), since `>` is its
    // prefix — otherwise `>>` would parse as commands-mode with a stray `>`.
    if (raw.startsWith(">>"))
        return { mode: { type: "dev_commands" }, rest: raw.slice(2) };
    const head = raw[0];
    if (head === ">") return { mode: { type: "commands" }, rest: raw.slice(1) };
    if (head === "@")
        return { mode: { type: "resources" }, rest: raw.slice(1) };
    return { mode: { type: "everything" }, rest: raw };
};

// One token → the term(s) it contributes, in Resources mode. The `@`-sugar:
// an unquoted `kind:rest` whose prefix resolves to a known kind becomes a
// KindTerm over that kind, PLUS a text term for the trailing search (so
// `@s:acids` ≡ `type:source acids`). A resolved-but-empty rest is a lone
// KindTerm (`@s:` = all sources). Everything else falls back to the shipped
// `termOf` (which still honours `type:`/`by:`/`"quoted"`/text — composability).
const resourceTermsOf = (token: Token): readonly SearchTerm[] => {
    if (!token.quoted) {
        const colon = token.text.indexOf(":");
        if (colon > 0) {
            const resolved = resolveKindPrefix(
                token.text.slice(0, colon),
                SEARCH_KINDS,
            );
            if (resolved.type === "resolved") {
                const rest = token.text.slice(colon + 1);
                const kindTerm: SearchTerm = {
                    type: "kind",
                    kinds: [resolved.kind],
                };
                return rest === ""
                    ? [kindTerm]
                    : [kindTerm, { type: "text", value: rest }];
            }
            // Ambiguous / Unknown → degrade to text (via the shipped termOf).
        }
    }
    return [termOf(token)];
};

// The required entry point: a raw string → { mode, query }. Pure and total.
// Everything/Commands modes are ordinary queries over the residual; Resources
// mode adds the `@kind:` sugar per token.
const parseOmni = (raw: string): ParsedOmni => {
    const { mode, rest } = splitMode(raw);
    const tokens = tokenize(rest);
    const terms =
        mode.type === "resources"
            ? tokens.flatMap(resourceTermsOf)
            : tokens.map(termOf);
    return { mode, query: { terms } };
};

// The refinement the dropdown reflects: the resolution of the FIRST unquoted
// token's kind-prefix, only in Resources mode and only when that token carries a
// colon (a refinement was attempted). `undefined` = no refinement in flight
// (show the mode's items / the ghost kind options).
const leadingRefinement = (raw: string): KindResolution | undefined => {
    const { mode, rest } = splitMode(raw);
    if (mode.type !== "resources") return undefined;
    const first = tokenize(rest)[0];
    if (first === undefined || first.quoted) return undefined;
    const colon = first.text.indexOf(":");
    if (colon <= 0) return undefined;
    return resolveKindPrefix(first.text.slice(0, colon), SEARCH_KINDS);
};

// Which command kinds a mode admits, before term filtering. `command` is not a
// SearchKind, so the command-vs-resource gate lives HERE (the within-resources
// kind narrowing is the KindTerm the parser already emitted). Structural on the
// kind string to avoid coupling to CommandPalette's `CommandKind` union.
const modeAdmits = (mode: QueryMode, kind: string): boolean => {
    switch (mode.type) {
        case "everything":
            // Dev commands ARE admitted in the default view (owner call): they
            // only exist in dev builds, so production stays clean by
            // construction. `>>` is a convenience FILTER, not the only door.
            return true;
        case "commands":
            return kind === "command";
        case "dev_commands":
            return kind === "dev-command";
        case "resources":
            return kind !== "command" && kind !== "dev-command";
    }
};
